/* @section: contact-page */
import { motion } from "framer-motion";
import { useState } from "react";
import PageLayout from "@/components/Layout";
import { orgInfo, geographicAreas } from "@/data";
import { Mail, MapPin, Phone, ExternalLink, PhoneCall } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as [number,number,number,number] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

function PageHero() {
  return (
    <section className="relative py-24 overflow-hidden" style={{ background: "linear-gradient(135deg, oklch(0.88 0.12 225), oklch(0.76 0.16 235))" }}>
      <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 40% 60%, oklch(0.99 0.01 50) 0%, transparent 60%)" }} />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold uppercase tracking-widest bg-white/50 px-3 py-1 rounded-full border border-primary/30" style={{ color: "oklch(0.35 0.15 228)" }}>Get In Touch</span>
            <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-4 leading-tight" style={{ color: "oklch(0.15 0.08 235)" }}>Contact Us</h1>
          </motion.div>
          <motion.p variants={fadeUp} className="text-lg leading-relaxed" style={{ color: "oklch(0.25 0.06 230)" }}>
            We welcome partnerships, donor inquiries, and collaboration requests from NGOs, CSOs, UN agencies, and government bodies.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function ContactContent() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", org: "", subject: "", message: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mailtoLink = `mailto:${orgInfo.email}?subject=${encodeURIComponent(form.subject || `Partnership Inquiry from ${form.name}`)}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nOrganization: ${form.org}\n\nMessage:\n${form.message}`)}`;
    window.location.href = mailtoLink;
    setSubmitted(true);
  };

  const contactChannels = [
    { label: "General Inquiries", value: orgInfo.email, icon: <Mail className="w-4 h-4" />, href: `mailto:${orgInfo.email}`, note: "General enquiries, partnerships, programmes" },
    { label: "Executive Director", value: orgInfo.emailDirector, icon: <Mail className="w-4 h-4" />, href: `mailto:${orgInfo.emailDirector}`, note: "Organizational leadership & strategic partnerships" },
    { label: "PSEA Focal Point", value: orgInfo.emailPSEA, icon: <Mail className="w-4 h-4" />, href: `mailto:${orgInfo.emailPSEA}`, note: "Safeguarding, PSEA, and accountability concerns" },
    { label: "Phone", value: orgInfo.phone, icon: <Phone className="w-4 h-4" />, href: `tel:${orgInfo.phone}`, note: "Office hours: Mon–Fri, 9:00–17:00 MMT" },
    { label: "Hotline", value: orgInfo.hotline, icon: <PhoneCall className="w-4 h-4" />, href: `tel:${orgInfo.hotline}`, note: "Emergency & safeguarding concerns" },
  ];

  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Our Offices</span>
              <h2 className="text-3xl font-bold mt-2 mb-6">Find Us</h2>
            </motion.div>

            {/* Offices */}
            <motion.div variants={fadeUp} className="space-y-4 mb-8">
              <div className="flex gap-4 p-5 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-sm mb-1">Head Office — Ywangan Township</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{orgInfo.address}</p>
                </div>
              </div>

              <div className="flex gap-4 p-5 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-sm">Sub-Office — Nyaung Shwe Township</p>
                    <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full font-medium">Field Office</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Nyaung Shwe Township, Southern Shan State, Myanmar</p>
                </div>
              </div>
            </motion.div>

            {/* Contact channels */}
            <motion.div variants={fadeUp}>
              <h3 className="font-bold text-base mb-4">Contact Channels</h3>
              <div className="space-y-3">
                {contactChannels.map((ch) => (
                  <a key={ch.label} href={ch.href} className="flex gap-4 p-4 rounded-xl bg-card border border-border hover:border-primary/30 hover:shadow-md transition-all group">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                      {ch.icon}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground font-medium mb-0.5">{ch.label}</p>
                      <p className="text-sm font-semibold text-primary group-hover:underline break-all">{ch.value}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{ch.note}</p>
                    </div>
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="p-8 rounded-2xl bg-card border border-border">
              <h3 className="font-bold text-xl mb-1">Send Us a Message</h3>
              <p className="text-sm text-muted-foreground mb-6">We respond to all inquiries within 3 working days.</p>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="text-5xl mb-4">✅</div>
                  <h4 className="font-bold text-lg mb-2">Your Message is Ready!</h4>
                  <p className="text-sm text-muted-foreground">Your email client should open with your message pre-filled. Thank you for reaching out to FLYN.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Full Name *</label>
                      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                        placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Email Address *</label>
                      <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                        placeholder="you@organisation.org" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Organisation / Institution</label>
                    <input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                      placeholder="NGO, UN Agency, donor, government body, etc." />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Subject *</label>
                    <select required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all">
                      <option value="">Select inquiry type</option>
                      <option value="Partnership / Collaboration">Partnership / Collaboration</option>
                      <option value="Funding / Donor Inquiry">Funding / Donor Inquiry</option>
                      <option value="Programme Information">Programme Information</option>
                      <option value="Media / Communications">Media / Communications</option>
                      <option value="Safeguarding / PSEA Concern">Safeguarding / PSEA Concern</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Message *</label>
                    <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={5} className="w-full px-4 py-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none"
                      placeholder="Describe your inquiry, partnership interest, or how you would like to collaborate with FLYN..." />
                  </div>
                  <button type="submit" className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
                    style={{ background: "linear-gradient(135deg, oklch(0.50 0.19 228), oklch(0.40 0.22 235))", color: "white", boxShadow: "0 8px 24px oklch(0.50 0.19 228 / 0.35)" }}>
                    <Mail className="w-4 h-4" />
                    Send Message
                    <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                  </button>
                  <p className="text-xs text-muted-foreground text-center">This will open your email client with your message pre-filled and send to {orgInfo.email}</p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function AreasCoverage() {
  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-8">
          <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Programme Coverage</span>
          <h2 className="text-2xl md:text-3xl font-bold mt-2">Areas of Operation</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {geographicAreas.map((area) => (
            <motion.div key={area.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className="p-6 rounded-2xl bg-background border border-border hover:border-primary/30 hover:shadow-md transition-all">
              <MapPin className="w-6 h-6 text-primary mb-3" />
              <h3 className="font-bold text-base mb-1">{area.name}</h3>
              <p className="text-xs text-muted-foreground mb-3">Southern Shan State, Myanmar</p>
              <div className="flex flex-wrap gap-1.5">
                {area.programs.map((p) => (
                  <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium border border-primary/20">{p}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PartnerCTA() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center p-10 rounded-3xl border border-border bg-card">
          <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Why Partner With FLYN?</span>
          <h2 className="text-2xl md:text-3xl font-bold mt-2 mb-4">A Trusted Local Partner</h2>
          <p className="text-muted-foreground leading-relaxed mb-8 max-w-xl mx-auto">
            FLYN brings deep community trust, local knowledge, and proven field experience. We offer transparent financial management, strong accountability frameworks, and GEDSI-sensitive programming aligned with humanitarian standards.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { value: "8+", label: "Years Active" },
              { value: "3", label: "Townships" },
              { value: "6+", label: "Donor Partners" },
              { value: "14K+", label: "Beneficiaries" },
            ].map((s) => (
              <div key={s.label} className="p-4 rounded-xl bg-muted/50 border border-border text-center">
                <p className="text-2xl font-bold font-mono text-primary">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function Contact() {
  return (
    <PageLayout>
      <PageHero />
      <ContactContent />
      <AreasCoverage />
      <PartnerCTA />
    </PageLayout>
  );
}
